/* @flow */

import { call, put, take, select } from 'redux-saga/effects'
import { includes } from 'lodash'

import type { Action } from '../types'

import { sagaPost, sagaPut } from '../fetch'
import { API_POLLS_URL, apiUrlForPollWhereId } from '../urls'
import {
  BadResponseError,
  SagaMalfunctionError,
  sagaHandleError,
  UnauthorizedError,
  AlertDangerError,
} from '../helpers'
import { getLegacyPollFromPoll } from '../legacy'

import { setAlertSuccess } from '../stores/alert'
import { goToPollWhereId } from '../stores/routing'
import { setIsCreatingPoll, setIsSavingPollWhereId } from '../stores/pending'
import {
  selectCheckedoutPoll,
  selectIsCheckedoutPollNew,
} from '../stores/pollBuilderCheckedoutPoll'

import { sagaInsertLegacyPollsIntoStore } from './fetchPoll'
import { sagaLogoutUnauthorizedUser } from './logout'

export const POLL_BUILDER_SAVE_BUTTON_CLICK: string =
  'opinary/POLL_BUILDER_SAVE_BUTTON_CLICK'

export function pollBuilderSaveButtonClick(): Action {
  return {
    type: POLL_BUILDER_SAVE_BUTTON_CLICK,
  }
}

export function* sagaPollBuilderSaveButtonClickExisting(): Generator<
  *,
  void,
  *
> {
  const poll = yield select(selectCheckedoutPoll)

  if (poll == null) throw new SagaMalfunctionError()

  const legacyRequestBody = getLegacyPollFromPoll(poll)

  const response = yield call(
    sagaPut,
    apiUrlForPollWhereId(poll.id),
    legacyRequestBody
  )
  if (response == null) throw new SagaMalfunctionError()
  if (response.status === 401 || response.status === 403) {
    yield call(sagaLogoutUnauthorizedUser)
    throw new UnauthorizedError()
  }
  if (response.status !== 200) throw new BadResponseError(response)

  yield call(sagaInsertLegacyPollsIntoStore, [legacyRequestBody])

  yield put(setAlertSuccess('Poll Saved'))
}

export function* sagaPollBuilderSaveButtonClickNew(): Generator<*, void, *> {
  const poll = yield select(selectCheckedoutPoll)
  if (poll == null) throw new SagaMalfunctionError()

  const legacyRequestBody = getLegacyPollFromPoll(poll)

  const response = yield call(sagaPost, API_POLLS_URL, legacyRequestBody)
  if (response == null) throw new SagaMalfunctionError()
  if (response.status === 401 || response.status === 403) {
    return yield call(sagaLogoutUnauthorizedUser)
  }

  if (response.status === 400) {
    const body = yield call([response, response.json])
    if (body == null) throw new SagaMalfunctionError()
    if (includes(body.errors, 'invalidArguments')) {
      debugger
      throw new AlertDangerError('Please enter a name and select a customer')
    }
    if (includes(body.errors, 'pollNameNotUnique')) {
      throw new AlertDangerError(
        'A poll with that name already exists. Please choose a different name.'
      )
    } else if (includes(body.errors, 'invalidCustomerName')) {
      throw new AlertDangerError('Unknown customer name')
    } else {
      throw new BadResponseError(response)
    }
  }
  if (response.status !== 201) throw new BadResponseError(response)

  const createdLegacyPoll = yield call([response, response.json])
  if (createdLegacyPoll == null) throw new SagaMalfunctionError()

  yield call(sagaInsertLegacyPollsIntoStore, [createdLegacyPoll])

  // this checks out the newly created poll on mount
  yield put(goToPollWhereId(createdLegacyPoll.id))

  yield put(setAlertSuccess('Poll successfully created'))
}

export function* sagaPollBuilderSaveButtonClick(): Generator<*, void, *> {
  const isNew = yield select(selectIsCheckedoutPollNew)
  if (isNew == null) throw new SagaMalfunctionError()

  const poll = yield select(selectCheckedoutPoll)
  if (poll == null) throw new SagaMalfunctionError()

  if (poll.name.trim() === '') {
    throw new AlertDangerError('Please enter a name')
  }
  if (poll.customer == null) {
    throw new AlertDangerError('Please select a customer')
  }

  if (isNew) {
    yield put(setIsCreatingPoll(true))
    try {
      yield call(sagaPollBuilderSaveButtonClickNew)
    } catch (err) {
      yield put(setIsCreatingPoll(false))
      throw err
    }
    yield put(setIsCreatingPoll(false))
  } else {
    yield put(setIsSavingPollWhereId(poll.id, true))
    try {
      yield call(sagaPollBuilderSaveButtonClickExisting)
    } catch (err) {
      yield put(setIsSavingPollWhereId(poll.id, false))
      throw err
    }
    yield put(setIsSavingPollWhereId(poll.id, false))
  }
}

export function* sagaRepeatedlyPollBuilderSaveButtonClick(): Generator<
  *,
  void,
  *
> {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    yield take(POLL_BUILDER_SAVE_BUTTON_CLICK)

    try {
      yield call(sagaPollBuilderSaveButtonClick)
    } catch (err) {
      yield call(sagaHandleError, err)
    }
  }
}



// WEBPACK FOOTER //
// ./src/sagas/savePoll.js